<template>
  <el-container direction="vertical" style="margin: 5vh 5vw">
    <job-new @jobSubmitted="createJob" :isVisible.sync="showNewModal" />
    <div class="header" style="margin-bottom: 25px">
      <h2>
        Manage Packages
        <el-tooltip placement="right" effect="light">
          <span slot="content"
            >A package is a series of activities to be processed in the order
            you<br />determine. Packages may contain DataMaestro tasks and/or
            TurboIntegrator<br />processes. They can be scheduled in advance or
            run on demand.</span
          >
          <i
            class="el-icon-info"
            style="position: relative; left: 2px; bottom: 12px; font-size: 1rem"
          />
        </el-tooltip>
      </h2>
    </div>
    <div>
      <el-input
        v-model="filter"
        placeholder="Type to search"
        style="width: 30%; margin-bottom: 12px"
      />
      <el-button
        class="button"
        @click="pushHistory"
        :disabled="envRights === 'Run'"
        style="
          display: flex;
          justify-content: center;
          padding: 18px;
          width: 125px;
          height: 40px;
          float: right;
          line-height: 15%;
        "
      >
        View All History</el-button
      >
    </div>
    <el-table
      :data="itemsToShow"
      :default-sort="{ prop: 'name', order: 'ascending' }"
      style="border: 2px solid #ebeef5; width: 100%"
      @row-dblclick="pushDetails"
      max-height="60vh"
    >
      <div slot="empty">
        A package is a series of activities to be processed in the order you
        determine. Packages may contain DataMaestro tasks and/or TurboIntegrator
        processes. Click the
        <b>Add Package</b> button to get started with your first package.
      </div>
      <el-table-column width="240">
        <template slot="header">
          <el-button
            @click="showNewModal = true"
            class="button"
            :disabled="envRights === 'Run'"
            icon="el-icon-plus"
            >Add Package</el-button
          >
        </template>
        <template slot-scope="scope">
          <el-button-group>
            <el-tooltip placement="bottom" content="Run Package">
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-caret-right"
                @click="runJob(scope.row)"
            /></el-tooltip>
            <el-tooltip
              placement="bottom"
              :content="envRights === 'Run' ? 'View Package' : 'Edit Package'"
            >
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-edit"
                @click="push(scope.row, 'packagesdetails')"
            /></el-tooltip>
            <el-tooltip
              placement="bottom"
              :content="envRights === 'Run' ? 'View Schedule' : 'Edit Schedule'"
            >
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-alarm-clock"
                @click="push(scope.row, 'packagesschedule')"
            /></el-tooltip>
            <el-tooltip placement="bottom" content="View History">
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-reading"
                @click="push(scope.row, 'packageshistory')"
            /></el-tooltip>
            <el-tooltip
              placement="bottom"
              content="Delete Package"
              v-if="envRights !== 'Run'"
            >
              <el-button
                size="medium"
                class="table-button"
                icon="el-icon-delete"
                @click="deleteJob(scope.row)"
            /></el-tooltip>
            <el-button
              size="medium"
              class="hidden"
              icon="el-icon-delete"
              v-else
            />
          </el-button-group>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="Name" sortable :min-width="25" />
      <el-table-column
        prop="scheduled"
        label="Scheduled"
        sortable
        :min-width="15"
      />
      <el-table-column
        prop="createdBy"
        label="Created By"
        sortable
        :min-width="20"
      />
      <el-table-column
        prop="updatedBy"
        label="Last Modified By"
        sortable
        :min-width="20"
      />
      <el-table-column
        prop="updatedAt"
        label="Last Modified At"
        sortable
        :sort-method="sortDate"
        :min-width="20"
      />
    </el-table>
  </el-container>
</template>

<script>
import { mapState } from "vuex";
import JobNew from "../components/modals/JobNew";
import JobService from "../services/JobService";
import moment from "moment-timezone";
import Helper from "../utilities/Helper";

export default {
  props: {
    savedJobDetails: Object,
  },
  watch: {
    savedJobDetails(value) {
      const index = this.items.findIndex((item) => item.id === value.job.id);
      const row = {
        id: value.job.id,
        name: value.job.name,
        scheduled: value.job.scheduled ? "Yes" : "No",
        createdBy: value.userEmail,
        updatedBy: value.userEmail,
        updatedAt: moment(value.job.updatedAt)
          .tz(this.timezone)
          .format("h:mm:ss A on LL"),
      };
      if (index === -1) {
        this.items.push(row);
      } else {
        this.$set(this.items, index, row);
      }
    },
    filter(value) {
      let filter = value.toLowerCase();
      this.itemsToShow = this.items.filter((item) => {
        return JSON.stringify(item).toLowerCase().includes(filter);
      });
    },
  },
  components: { JobNew },
  computed: {
    ...mapState([
      "userId",
      "instanceId",
      "envRights",
      "isUserLoggedIn",
      "environmentId",
    ]),
  },
  data() {
    return {
      showNewModal: false,
      items: [],
      itemsToShow: [],
      filter: null,
      timezone:
        process.env.VUE_APP_SERVER_TIMEZONE ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  },
  methods: {
    handleError: Helper.handleError,
    trim: Helper.trim,
    pushDetails(row) {
      this.push(row, "packagesdetails");
    },
    push(row, name) {
      this.$store.dispatch("setJobId", row.id);
      this.$store.dispatch("setCurrentJobName", row.name);
      this.$router.push({ name });
    },
    pushHistory() {
      this.$router.push({ name: "packageAllHistory" });
    },
    /**
     * Runs the package
     * @function
     * @param {Object} row - Table row of the selected package
     */
    async runJob(row) {
      if (confirm(`Are you sure you want to run the "${row.name}" package?`)) {
        const loading = this.$loading({
          lock: true,
          //text: "Working",
          background: "rgba(0, 0, 0, 0.3)",
        });
        try {
          await JobService.run({
            jobId: row.id,
            userId: this.userId,
          });

          this.$message({
            type: "success",
            message: `The "${row.name}" package has been kicked off successfully. You'll receive an email when it finishes running. Check the package's history log for updates until then.`,
            duration: 5000,
            showClose: true,
          });
        } catch (err) {
          this.handleError(err, this, this.instanceId);
        } finally {
          loading.close();
        }
      }
    },
    /**
     * Deletes a package
     * @function
     * @param {Object} row - Table row of the selected package
     */
    async deleteJob(row) {
      if (
        confirm(`Are you sure you want to delete the "${row.name}" package?`)
      ) {
        const loading = this.$loading({
          lock: true,
          //text: "Working",
          background: "rgba(0, 0, 0, 0.3)",
        });
        try {
          await JobService.delete({
            jobId: row.id,
          });
          this.getJobs();
          this.$message({
            type: "success",
            message: `"${row.name}" package deleted successfully`,
            duration: 5000,
            showClose: true,
          });
        } catch (err) {
          this.handleError(err, this, this.instanceId);
        } finally {
          loading.close();
        }
      }
    },
    /**
     * Creates a new package
     * @function
     * @param {String} name - Name of the package
     */
    async createJob(name) {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        name = this.trim(name);
        const response = await JobService.create({
          instanceId: this.instanceId,
          createdBy: this.userId,
          updatedBy: this.userId,
          name,
          scheduled: false,
        });
        const job = response.data.job;
        const userEmail = response.data.userEmail;
        const item = {
          id: job.id,
          name: job.name,
          scheduled: job.scheduled ? "Yes" : "No",
          createdBy: userEmail,
          updatedBy: userEmail,
          updatedAt: moment(job.updatedAt)
            .tz(this.timezone)
            .format("h:mm:ss A on LL"),
        };
        this.items.push(item);
        this.$store.dispatch("setJobId", job.id);
        this.$store.dispatch("setCurrentJobName", job.name);
        this.$router.push({ name: "packagesdetails" });
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async getJobs() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        const response = await JobService.show({
          instanceId: this.instanceId,
        });
        const jobs = response.data.jobs;
        const users = response.data.users;
        let items = [];
        this.items = [];
        for (let i = 0; i < jobs.length; i++) {
          const createdByUsers = users.find(
            (user) => user.id === jobs[i].createdBy
          );
          const updatedByUsers = users.find(
            (user) => user.id === jobs[i].updatedBy
          );
          items[i] = {
            id: jobs[i].id,
            name: jobs[i].name,
            scheduled: jobs[i].scheduled ? "Yes" : "No",
            createdBy: createdByUsers.displayName,
            updatedBy: updatedByUsers.displayName,
            updatedAt: moment(jobs[i].updatedAt)
              .tz(this.timezone)
              .format("h:mm:ss A on LL"),
          };
          this.items.push(items[i]);
        }
        this.itemsToShow = this.items;
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * The purpose: The purpose is to have a sorting algo for the custom sort.
     * @param {object} a is a object that has a date
     * @param {object} b is a object that has a date
     */
    sortDate: function (a, b) {
      return (
        this.toFloatFromDate(a.updatedAt) - this.toFloatFromDate(b.updatedAt)
      );
    },
    toFloatFromDate: function (num) {
      let date = new Date(num.split(" on ").join(" "));
      return date;
    },
  },
  mounted() {
    if (!this.isUserLoggedIn) {
      this.$router.push({ name: "login" });
    } else if (!this.instanceId) {
      this.$router.push({ name: "servers" });
    } else {
      this.getJobs();
      if (this.jobId) this.$store.dispatch("setJobId", null);
      if (this.currentJobName) this.$store.dispatch("setCurrentJobName", null);
    }
  },
};
</script>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
}
.button {
  background-color: #b3282d;
  color: white;
  border-color: white;
  padding: 10px 15px;
}
.button:focus {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.button:hover {
  background-color: #9a0f14;
  color: white;
  border-color: white;
  outline: none;
}
.table-button {
  background-color: inherit;
  color: #606266;
  border: none;
  outline: none;
  font-size: 1.15rem;
}
.table-button:focus {
  background-color: inherit;
  color: #606266;
  border: none;
  outline: none;
}
.table-button:hover {
  background-color: #ecf5fe;
  color: #606266;
  border: none;
  outline: none;
}
.hidden:focus {
  background-color: inherit;
  color: #f0f0f0;
  border: none;
  outline: none;
}
.hidden:hover {
  background-color: inherit;
  color: #f0f0f0;
  border: none;
  outline: none;
}
.hidden {
  background-color: inherit;
  color: #f0f0f0;
  border: none;
  outline: none;
  font-size: 1.15rem;
}
</style>
